import React, { useEffect } from 'react';
import { HelmetProvider } from "react-helmet-async";
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import UploadAndJobDescription from './components/UploadAndJobDescription';
import ResultsPage from './components/ResultsPage';
import GenerateResume from "./components/GenerateResume";
import Footer from './components/Footer';
import Login from './components/Login';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import FAQ from "./components/FAQ";
import About from "./components/About";
import Contact from "./components/Contact";
import ZamAgent from "./components/ZamAgent";
import PricingPage from './components/PricingPage';
import SuccessPage from './components/SuccessPage';
import CookieConsent from "react-cookie-consent";
import CoverLetterPage from "./components/CoverLetterPage";
import InterviewPrepPage from "./components/InterviewPrepPage";
import Home from "./components/Home"; // ✅ NEW IMPORT
import ExpertReviewPage from "./components/ExpertReviewPage";
import SEO from "./components/SEO";
import routeSeo from "./seo/routeSeo";
import { StatusCard } from "./components/PageShells";
import { MapPinOff } from "lucide-react";

import { upsertUserProfile } from "./utils/supabaseHelpers";
import { supabase } from "./supabaseClient";

import './App.css';

// ✅ Phase 3: Jobs Portal Imports
import JobsLayout from "./components/jobs/JobsLayout";
import AllJobsPage from "./components/jobs/AllJobsPage";
import SavedJobsPage from "./components/jobs/SavedJobsPage";
import RecommendedJobsPage from "./components/jobs/RecommendedJobsPage";
import JobDetailsPage from "./components/jobs/JobDetailsPage";
import AppliedJobsPage from "./components/jobs/AppliedJobsPage";

function Page({ seo, children }) {
  return (
    <>
      <SEO {...seo} />
      {children}
    </>
  );
}

function UploadResume() {
  return (
    <div className="bg-gradient-to-r from-background to-neutral min-h-screen flex items-center justify-center animate-fade-in">
      <UploadAndJobDescription />
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-[70vh] bg-gradient-to-b from-slate-50 to-blue-50/50 px-4 py-16 sm:py-24">
      <StatusCard
        icon={MapPinOff}
        eyebrow="404"
        title="Page not found"
        primary={{ to: "/", label: "Return Home" }}
        secondary={{ to: "/upload", label: "Resume Optimizer" }}
      >
        The page you requested may have moved or no longer exists. Use one of the links below to get back on track.
      </StatusCard>
    </div>
  );
}

function App() {
  useEffect(() => {
    const syncUserProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await upsertUserProfile(user);
      }
    };
    syncUserProfile();
  }, []);

  return (
    <HelmetProvider>
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-grow pt-6 md:pt-10">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Page seo={routeSeo.home}><Home /></Page>} />
          <Route path="/about" element={<Page seo={routeSeo.about}><About /></Page>} />
          <Route path="/contact" element={<Page seo={routeSeo.contact}><Contact /></Page>} />
          <Route path="/login" element={<Page seo={routeSeo.login}><Login /></Page>} />
          <Route path="/terms" element={<Page seo={routeSeo.terms}><Terms /></Page>} />
          <Route path="/privacy" element={<Page seo={routeSeo.privacy}><Privacy /></Page>} />
          <Route path="/faq" element={<Page seo={routeSeo.faq}><FAQ /></Page>} />
          <Route path="/expert-review" element={<Page seo={routeSeo.expertReview}><ExpertReviewPage /></Page>} />
          <Route path="/zam-agent" element={<Page seo={routeSeo.zamAgent}><ZamAgent /></Page>} />

          {/* Protected Routes */}
          <Route
            path="/upload"
            element={
              <ProtectedRoute>
                <Page seo={routeSeo.upload}><UploadResume /></Page>
              </ProtectedRoute>
            }
          />
          <Route
            path="/results"
            element={
              <ProtectedRoute>
                <Page seo={routeSeo.results}><ResultsPage /></Page>
              </ProtectedRoute>
            }
          />
          <Route
            path="/generate-resume"
            element={
              <ProtectedRoute>
                <Page seo={routeSeo.generateResume}><GenerateResume /></Page>
              </ProtectedRoute>
            }
          />

          {/* Payments */}
          <Route path="/pricing" element={<Page seo={routeSeo.pricing}><PricingPage /></Page>} />
          <Route path="/success" element={<Page seo={routeSeo.success}><SuccessPage /></Page>} />

          {/* Phase 2 Features */}
          <Route
            path="/cover-letter"
            element={
              <ProtectedRoute>
                <Page seo={routeSeo.coverLetter}><CoverLetterPage /></Page>
              </ProtectedRoute>
            }
          />
          <Route
            path="/interview-prep"
            element={
              <ProtectedRoute>
                <Page seo={routeSeo.interviewPrep}><InterviewPrepPage /></Page>
              </ProtectedRoute>
            }
          />

          {/* Phase 3: UK Jobs Portal */}
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <Page seo={routeSeo.jobs}><JobsLayout /></Page>
              </ProtectedRoute>
            }
          >
            <Route index element={<AllJobsPage />} />
            <Route path="saved" element={<SavedJobsPage />} />
            <Route path="applied" element={<AppliedJobsPage />} />
            <Route path="recommended" element={<RecommendedJobsPage />} />
          </Route>

          {/* Better Job Details Route */}
          <Route
            path="/jobs/:id"
            element={
              <ProtectedRoute>
                <Page seo={routeSeo.jobs}>
                  <JobDetailsPage />
                </Page>
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Page seo={routeSeo.notFound}><NotFound /></Page>} />
        </Routes>
      </main>

      {/* GDPR Cookie Banner */}
      <CookieConsent
        location="bottom"
        buttonText="Accept"
        declineButtonText="Reject"
        enableDeclineButton
        containerClasses="jobzam-cookie-banner"
        contentClasses="jobzam-cookie-copy"
        buttonClasses="jobzam-cookie-button jobzam-cookie-accept"
        declineButtonClasses="jobzam-cookie-button jobzam-cookie-reject"
        style={{ zIndex: 9999 }}
        expires={150}
      >
        We use cookies and similar technologies to enhance your experience,
        analyze site traffic, and personalize content. By clicking "Accept",
        you agree to our use of cookies.{" "}
        <a href="/privacy" className="jobzam-cookie-link">
          Learn more
        </a>.
      </CookieConsent>

      <Footer />
    </div>
    </HelmetProvider>
  );
}

export default App;
